// web/src/components/ceremony/JoinUnsealModal.tsx — the "Join an unseal" entry
// point (content-guide §1.3 consequential). A holder who was handed a join code
// (what3words words) + a one-time PIN enters them here; on submit we resolve the
// OPEN ceremony via api.joinUnseal and route into the animated UnsealCeremony with
// a holder context. The ceremony then fetches the holder's own opaque shard
// (my-shard) and submits as today — this modal carries NO shard, key, or plaintext
// (R11), only the public join code + PIN that resolve the ceremony.
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import Modal from '../Modal';
import { Icon } from '../ui/Icon';
import { FeedbackMessage } from '../ui';
import { api } from '../../api';
import type { CeremonyContext } from './UnsealCeremony';

export function JoinUnsealModal({
  onClose,
}: {
  onClose: () => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [joinWords, setJoinWords] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = joinWords.trim().length > 0 && pin.trim().length > 0 && !busy;

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const ctx = await api.joinUnseal(joinWords.trim(), pin.trim());
      // Route into the ceremony with a HOLDER context. The container self-hydrates
      // the holder's own shard (my-shard) + roster from the secretId; we pass the
      // record name + requester's ephemeral key so its share is sealed to the
      // requester before it leaves this device (R11).
      const ceremony: CeremonyContext = {
        recordName: ctx.recordName,
        thresholdK: 0, // hydrated from the record detail in the ceremony
        totalN: 0,
        requesterLabel: 'the requester',
        holders: [],
        role: 'holder',
      };
      navigate(`/unseal/${ctx.requestId}`, { state: { ceremony } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [joinWords, pin, navigate]);

  return (
    <Modal
      title="Join an unseal"
      subtitle="Help break the glass"
      maxWidth={460}
      dismissable={!busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSend}
          >
            {busy ? <span className="spinner" aria-hidden="true" /> : <Icon icon={KeyRound} size="sm" />}
            Join ceremony
          </button>
        </>
      }
    >
      <p style={{ color: 'var(--color-text2)', marginTop: 0, lineHeight: 1.5 }}>
        Enter the join code and one-time PIN the requester shared with you. They let
        you into <strong style={{ color: 'var(--color-text)' }}>this</strong> ceremony
        and nothing else — your shard is only decrypted on your device, when you choose
        to submit it.
      </p>

      <label className="field-label" htmlFor="join-words">
        Join code (three words)
      </label>
      <input
        id="join-words"
        className="input"
        value={joinWords}
        onChange={(e) => setJoinWords(e.target.value)}
        disabled={busy}
        placeholder="e.g. amber-relay-cobalt"
        autoComplete="off"
      />

      <label className="field-label" htmlFor="join-pin" style={{ marginTop: '0.8rem' }}>
        One-time PIN
      </label>
      <input
        id="join-pin"
        className="input"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        disabled={busy}
        placeholder="6-digit PIN"
        inputMode="numeric"
        autoComplete="off"
      />

      {error && <FeedbackMessage ok={false} msg={error} />}
    </Modal>
  );
}

export default JoinUnsealModal;
